import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import useAPI from '../../../../../api';
import { useAtom, useAtomValue } from 'jotai';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  nodeAtomFamily,
  selectedNodeIdAtom,
  validationAtom,
  ROOT_ID,
} from '../store/schemaAtoms';
import {
  NODE_TYPES,
  PRIMITIVE_TYPES,
  WIDGETS,
  TRANSLATIONS,
  LOOKUPS,
} from '../store/constants';
import {
  CenteredFlexBox,
  FullSizeColumn,
} from '../../../../../Components/FlexBox';
import FetchAutoComplete from '../../../../../Components/Inputs/FetchAutoComplete';

const getOptionLabelFormat = (opt) => {
  if (!opt) return '';
  if (typeof opt === 'string') return opt;
  return `${opt.id} - ${opt.label}`;
};

const ConfigPane = function ConfigPane() {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const selectedId = useAtomValue(selectedNodeIdAtom);
  const [node, setNode] = useAtom(nodeAtomFamily(selectedId));
  const { errors } = useAtomValue(validationAtom);

  const myErrors = useMemo(
    () => errors.filter((e) => e.nodeId === selectedId).map((e) => e.message),
    [errors, selectedId]
  );

  const patch = useCallback(
    (p) => setNode((prev) => ({ ...prev, ...p })),
    [setNode]
  );
  const isRoot = selectedId === ROOT_ID;

  return (
    <FullSizeColumn>
      <CenteredFlexBox justifyContent="space-between" padding="8px" gap="8px">
        <TuneRoundedIcon fontSize="small" sx={{ color: '#b43e8f' }} />
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#b43e8f',
            flexGrow: 1,
          }}
        >
          {t('SCHEMA_CONFIGURATION')}
        </Typography>
        {myErrors.length > 0 && (
          <Tooltip
            title={errors
              .map((e) => {
                const key = `SCHEMA_ERR_${e}`;
                return intl.messages[key] ? t(key) : e;
              })
              .join(', ')}
            placement="top"
          >
            <Chip
              size="small"
              label={intl.formatMessage(
                { id: 'SCHEMA_ISSUES' },
                { count: myErrors.length }
              )}
              icon={
                <ErrorOutlineRoundedIcon fontSize="small" color="inherit" />
              }
              sx={{
                ml: 'auto',
                height: 20,
                fontWeight: 600,
                backgroundColor: 'rgba(192, 57, 43, 0.14)',
                color: '#c0392b',
              }}
            />
          </Tooltip>
        )}
      </CenteredFlexBox>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
        {isRoot ? (
          <RootBanner />
        ) : (
          <Stack spacing={2.5}>
            <TextField
              size="small"
              label={t('SCHEMA_PROPERTY_NAME')}
              value={node.name}
              onChange={(e) => patch({ name: sanitizeName(e.target.value) })}
              error={myErrors.includes('EMPTY_PROPERTY_NAME')}
              helperText={
                myErrors.includes('EMPTY_PROPERTY_NAME')
                  ? t('SCHEMA_PROPERTY_NAME_REQUIRED')
                  : t('SCHEMA_PROPERTY_NAME_HELPER')
              }
              required
            />

            <TextField
              size="small"
              select
              label={t('SCHEMA_NODE_KIND')}
              value={node.nodeType}
              onChange={(e) => patch({ nodeType: e.target.value })}
            >
              <MenuItem value={NODE_TYPES.PRIMITIVE}>
                {t('SCHEMA_PRIMITIVE_FIELD')}
              </MenuItem>
              <MenuItem value={NODE_TYPES.OBJECT}>
                {t('SCHEMA_OBJECT_NESTED')}
              </MenuItem>
              <MenuItem value={NODE_TYPES.ARRAY}>
                {t('SCHEMA_ARRAY_OF_OBJECTS')}
              </MenuItem>
            </TextField>

            {node.nodeType === NODE_TYPES.PRIMITIVE && (
              <PrimitiveConfig node={node} patch={patch} myErrors={myErrors} />
            )}

            {node.nodeType === NODE_TYPES.ARRAY && (
              <ArrayConfig node={node} patch={patch} />
            )}

            <TextField
              label={t('SCHEMA_TITLE_TRANSLATION_KEY')}
              value={node.titleKey || ''}
              onChange={(e) => patch({ titleKey: e.target.value })}
              helperText={t('SCHEMA_TITLE_HELPER')}
              size="small"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={!!node.required}
                  onChange={(e) => patch({ required: e.target.checked })}
                />
              }
              label={t('SCHEMA_REQUIRED')}
            />
          </Stack>
        )}
      </Box>
    </FullSizeColumn>
  );
};

const PrimitiveConfig = function PrimitiveConfig({ node, patch, myErrors }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  return (
    <>
      <TextField
        size="small"
        select
        label={t('SCHEMA_PRIMITIVE_TYPE')}
        value={node.primitiveType}
        onChange={(e) => patch({ primitiveType: e.target.value })}
      >
        {PRIMITIVE_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        size="small"
        select
        label={t('SCHEMA_UI_WIDGET')}
        value={node.widget}
        onChange={(e) => patch({ widget: e.target.value })}
      >
        <MenuItem value={WIDGETS.INPUT}>{t('SCHEMA_WIDGET_INPUT')}</MenuItem>
        <MenuItem value={WIDGETS.SELECT}>
          {t('SCHEMA_WIDGET_STATIC_SELECT')}
        </MenuItem>
        <MenuItem value={WIDGETS.DYNAMIC_SELECT}>
          {t('SCHEMA_WIDGET_DYNAMIC_SELECT')}
        </MenuItem>
        <MenuItem value="constant">
          {t('SCHEMA_WIDGET_CONSTANT')}
        </MenuItem>
      </TextField>

      {/* --- RENDERER PER COSTANTI PRIMITIVE --- */}
      {node.widget === 'constant' && (
        <Box
          sx={{
            p: 2,
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: 1,
            border: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 1.5, display: 'block', fontWeight: 'bold' }}
          >
            {t('SCHEMA_SET_CONSTANT_FIXED_VALUE')}
          </Typography>

          {node.primitiveType === 'string' && (
            <TextField
              size="small"
              fullWidth
              label={t('SCHEMA_TEXT_VALUE')}
              value={node.constantValue || ''}
              onChange={(e) => patch({ constantValue: e.target.value })}
            />
          )}

          {(node.primitiveType === 'number' ||
            node.primitiveType === 'integer') && (
            <TextField
              size="small"
              fullWidth
              type="number"
              label={t('SCHEMA_NUMERIC_VALUE')}
              value={node.constantValue ?? ''}
              onChange={(e) =>
                patch({
                  constantValue:
                    e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          )}

          {node.primitiveType === 'boolean' && (
            <FormControlLabel
              control={
                <Switch
                  checked={
                    node.constantValue === true || node.constantValue === 'true'
                  }
                  onChange={(e) => patch({ constantValue: e.target.checked })}
                />
              }
              label={
                node.constantValue ? t('SCHEMA_TRUE') : t('SCHEMA_FALSE')
              }
            />
          )}
        </Box>
      )}

      {/* --- RENDERER PER GLI ALTRI WIDGET (Input, Select, ecc.) --- */}
      {node.widget !== 'constant' && (
        <>
          {/* RENDERER PER VALORE DI DEFAULT */}
          <Box sx={{ mt: 1, mb: 1 }}>
            {node.primitiveType === 'string' && (
              <TextField
                size="small"
                fullWidth
                label={t('SCHEMA_DEFAULT_VALUE')}
                value={node.defaultValue || ''}
                onChange={(e) => patch({ defaultValue: e.target.value })}
              />
            )}

            {(node.primitiveType === 'number' ||
              node.primitiveType === 'integer') && (
              <TextField
                size="small"
                fullWidth
                type="number"
                label={t('SCHEMA_DEFAULT_VALUE')}
                value={node.defaultValue ?? ''}
                onChange={(e) =>
                  patch({
                    defaultValue:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            )}

            {node.primitiveType === 'boolean' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      node.defaultValue === true || node.defaultValue === 'true'
                    }
                    onChange={(e) => patch({ defaultValue: e.target.checked })}
                  />
                }
                label={intl.formatMessage(
                  { id: 'SCHEMA_DEFAULT_BOOLEAN' },
                  {
                    value: node.defaultValue
                      ? t('SCHEMA_TRUE')
                      : t('SCHEMA_FALSE'),
                  }
                )}
              />
            )}
          </Box>

          {/* SOLO PER LE STRINGHE: Mostra Max Length */}
          {node.widget === WIDGETS.INPUT && node.primitiveType === 'string' && (
            <TextField
              size="small"
              type="number"
              label={t('SCHEMA_MAX_LENGTH')}
              value={node.maxLength ?? ''}
              onChange={(e) =>
                patch({
                  maxLength:
                    e.target.value === '' ? null : Number(e.target.value),
                })
              }
              inputProps={{ min: 1 }}
            />
          )}

          {node.widget === WIDGETS.INPUT &&
            (node.primitiveType === 'number' ||
              node.primitiveType === 'integer') && (
              <Stack direction="row" spacing={2}>
                <TextField
                  size="small"
                  type="number"
                  label={t('SCHEMA_MIN')}
                  value={node.min ?? ''}
                  onChange={(e) =>
                    patch({
                      min:
                        e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t('SCHEMA_MAX')}
                  value={node.max ?? ''}
                  onChange={(e) =>
                    patch({
                      max:
                        e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  sx={{ flex: 1 }}
                />
              </Stack>
            )}

          {node.widget === WIDGETS.SELECT && (
            <EnumEditor
              values={node.enumValues}
              onChange={(next) => patch({ enumValues: next })}
              error={myErrors.includes('SELECT_REQUIRES_AT_LEAST_ONE_OPTION')}
            />
          )}

          {node.widget === WIDGETS.DYNAMIC_SELECT && (
            <DynamicSelectConfig
              node={node}
              patch={patch}
              error={myErrors.includes('DYNAMIC_SELECT_REQUIRES_LOOKUP_KEY')}
            />
          )}
        </>
      )}
    </>
  );
};

const ArrayConfig = function ArrayConfig({ node, patch }) {
  const API = useAPI();
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  // ABILITIAMO L'OPZIONE COSTANTE
  const arrayMode =
    node.widget === WIDGETS.SELECT
      ? 'manual'
      : node.widget === WIDGETS.DYNAMIC_SELECT
        ? 'lookup'
        : node.widget === 'constant'
          ? 'constant'
          : 'dynamic';

  const handleModeChange = (e) => {
    const mode = e.target.value;
    if (mode === 'lookup') {
      patch({ widget: WIDGETS.DYNAMIC_SELECT, enumValues: [] });
    } else if (mode === 'manual') {
      patch({ widget: WIDGETS.SELECT, lookupKey: '' });
    } else if (mode === 'constant') {
      patch({ widget: 'constant', lookupKey: '' });
    } else if (mode === 'dynamic') {
      patch({ widget: WIDGETS.INPUT, lookupKey: '', enumValues: [] });
    }
  };

  const fetchLookups = (inputValue, onSuccess) =>
    API.lookupKeys(inputValue, (res) => {
      onSuccess(res);
    });

  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        select
        label={t('SCHEMA_ARRAY_BEHAVIOR')}
        value={arrayMode}
        onChange={handleModeChange}
      >
        <MenuItem value="dynamic">{t('SCHEMA_ARRAY_DYNAMIC')}</MenuItem>
        <MenuItem value="lookup">{t('SCHEMA_ARRAY_LOOKUP')}</MenuItem>
        <MenuItem value="manual">{t('SCHEMA_ARRAY_MANUAL')}</MenuItem>
        <MenuItem value="constant">{t('SCHEMA_ARRAY_CONSTANT')}</MenuItem>
      </TextField>

      {arrayMode === 'lookup' && (
        <FetchAutoComplete
          label={t('SCHEMA_ARRAY_LOOKUP_KEY')}
          placeholder={t('SCHEMA_ARRAY_LOOKUP_PLACEHOLDER')}
          selectedValue={
            LOOKUPS.find((l) => l.id === node.lookupKey) ||
            node.lookupKey ||
            null
          }
          onChangeSelectedValue={(e, val) =>
            patch({ lookupKey: val ? val.id || val : '' })
          }
          fetchCallback={fetchLookups}
          getOptionLabel={getOptionLabelFormat}
          noCustomValue
          size="small"
        />
      )}

      {/* --- RENDERER PER COSTANTE ARRAY --- */}
      {arrayMode === 'constant' && (
        <Box
          sx={{
            p: 2,
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: 1,
            border: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 1.5, display: 'block', fontWeight: 'bold' }}
          >
            {t('SCHEMA_ADD_CONSTANT_ARRAY_VALUES')}
          </Typography>
          <EnumEditor
            values={node.enumValues || []}
            onChange={(next) => patch({ enumValues: next })}
            isConstantMode
          />
        </Box>
      )}

      {arrayMode === 'manual' && (
        <EnumEditor
          values={node.enumValues || []}
          onChange={(next) => patch({ enumValues: next })}
        />
      )}
    </Stack>
  );
};

const EnumEditor = function EnumEditor({
  values,
  onChange,
  error,
  isConstantMode = false,
}) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const add = () => onChange([...values, { value: '', label: '' }]);
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const patchAt = (i, p) =>
    onChange(values.map((v, idx) => (idx === i ? { ...v, ...p } : v)));

  return (
    <Box
      sx={{
        border: error ? '1px solid #c0392b' : '1px solid rgba(0,0,0,0.12)',
        borderRadius: '10px',
        padding: '12px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: '#5A5A5A', letterSpacing: '0.06em' }}
        >
          {isConstantMode ? t('SCHEMA_ARRAY_VALUES') : t('SCHEMA_ENUM_OPTIONS')}
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title={t('SCHEMA_ADD_OPTION')}>
            <IconButton size="small" onClick={add}>
              <AddRoundedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {values.length === 0 ? (
        <Typography variant="caption" sx={{ color: '#c0392b' }}>
          {t('SCHEMA_ADD_AT_LEAST_ONE_OPTION')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {values.map((v, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label={t('VALUE')}
                value={v.value}
                onChange={(e) => patchAt(i, { value: e.target.value })}
                sx={{ flex: 1 }}
              />

              {!isConstantMode && (
                <TextField
                  size="small"
                  label={t('SCHEMA_LABEL')}
                  value={v.label || ''}
                  onChange={(e) => patchAt(i, { label: e.target.value })}
                  sx={{ flex: 2 }}
                />
              )}

              <IconButton size="small" onClick={() => remove(i)}>
                <DeleteOutlineRoundedIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

EnumEditor.propTypes = {
  values: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  isConstantMode: PropTypes.bool,
};

const DynamicSelectConfig = function DynamicSelectConfig({
  node,
  patch,
  error,
}) {
  const API = useAPI();
  const intl = useIntl();

  const t = (id) => intl.formatMessage({ id });
  const addParam = () =>
    patch({ lookupParams: [...node.lookupParams, { key: '', value: '' }] });
  const removeParam = (i) =>
    patch({
      lookupParams: node.lookupParams.filter((_, idx) => idx !== i),
    });
  const patchParam = (i, p) =>
    patch({
      lookupParams: node.lookupParams.map((lp, idx) =>
        idx === i ? { ...lp, ...p } : lp
      ),
    });

  const fetchLookups = (inputValue, onSuccess) =>
    API.lookupKeys(inputValue, (res) => {
      onSuccess(res);
    });

  return (
    <Stack spacing={2}>
      <FetchAutoComplete
        label={t('SCHEMA_LOOKUP_KEY')}
        placeholder={t('SCHEMA_LOOKUP_PLACEHOLDER')}
        selectedValue={node.lookupKey}
        onChangeSelectedValue={(e, val) =>
          patch({ lookupKey: val ? val.id || val : '' })
        }
        fetchCallback={fetchLookups}
        getOptionLabel={getOptionLabelFormat}
        error={error}
        helperText={
          error ? t('SCHEMA_LOOKUP_REQUIRED') : t('SCHEMA_LOOKUP_HELPER')
        }
        noCustomValue
        size="small"
      />

      <Box
        sx={{
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: '10px',
          padding: '12px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: '#5A5A5A', letterSpacing: '0.06em' }}
          >
            {t('SCHEMA_LOOKUP_PARAMETERS')}
          </Typography>
          <Tooltip title={t('SCHEMA_LOOKUP_PARAMS_INFO')}>
            <InfoOutlinedIcon
              fontSize="inherit"
              sx={{ ml: 0.5, color: '#999' }}
            />
          </Tooltip>
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title={t('SCHEMA_ADD_PARAMETER')}>
              <IconButton size="small" onClick={addParam}>
                <AddRoundedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {node.lookupParams.length === 0 ? (
          <Typography variant="caption" sx={{ color: '#888' }}>
            {t('SCHEMA_NO_PARAMETERS')}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {node.lookupParams.map((p, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label={t('SCHEMA_PARAM')}
                  value={p.key}
                  onChange={(e) => patchParam(i, { key: e.target.value })}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label={t('SCHEMA_VALUE_BINDING')}
                  value={p.value}
                  onChange={(e) => patchParam(i, { value: e.target.value })}
                  placeholder="$form.CountryId"
                  sx={{ flex: 2 }}
                />
                <IconButton size="small" onClick={() => removeParam(i)}>
                  <DeleteOutlineRoundedIcon fontSize="inherit" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

DynamicSelectConfig.propTypes = {
  node: PropTypes.object.isRequired,
  patch: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

const RootBanner = function RootBanner() {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  return (
    <Box
      sx={{
        border: '1px dashed rgba(0,0,0,0.16)',
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center',
      }}
    >
      <Typography variant="body2" sx={{ color: '#5A5A5A', mb: 1 }}>
        {t('SCHEMA_ROOT_LEVEL')}
      </Typography>
      <Typography variant="caption" sx={{ color: '#888' }}>
        {t('SCHEMA_ROOT_BANNER')}
      </Typography>
    </Box>
  );
};

const sanitizeName = (s) => s.replace(/\s+/g, '');

export default ConfigPane;
